import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { BookmarkIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import { useQuery } from '@tanstack/react-query'

import { API, constructApiUrl } from '../../../api/apis.config'
import {
  defaultEnrollmentOfficialDocumentsAvailability,
  getEnrollmentOfficialDocumentsAvailability
} from '../../../api/enrollments/get-enrollment-official-documents-availability'
import { getEnrollmentTermsAndConditions } from '../../../api/enrollments/get-enrollment-terms-and-conditions'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import ClientSpacePageTitle from '../../../components/pages/client-space/client-space-page-title'
import ClientPaymentInformation from '../../../components/pages/client-space/payments/client-payment-information'
import ResourceAccessItem from '../../../components/pages/client-space/resource-access-item'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { insuranceGeneralProvisionsLabelMap } from '../../../lib/types/enums/insurance/insurance-type.enum'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'

const ClientPaymentPage = () => {
  const { navigate } = useNavigator()
  const { enrollment } = useEnrollment()
  const { downloadDocuments } = useDownloadDocuments()

  const { data: officialDocumentsAvailability, isPending } = useQuery({
    queryKey: ['enrollment-official-documents-availability', enrollment.id],
    queryFn: () => getEnrollmentOfficialDocumentsAvailability(enrollment.id),
    initialData: defaultEnrollmentOfficialDocumentsAvailability
  })

  const { data: termsAndConditions, isPending: isTermsAndConditionsPending } = useQuery({
    queryKey: ['enrollment-terms-and-conditions', enrollment.id],
    queryFn: () => getEnrollmentTermsAndConditions(enrollment.id)
  })

  if (isPending) return <LoadingScreen />

  // a cancelled stay never takes place: its paid invoice and participation certificate never apply
  const isCancelled = EnrollmentProperties.isCancelled(enrollment)

  return (
    <div className="flex flex-col gap-4">
      <ClientSpacePageTitle
        icon={<BookmarkIcon color={tailwindColors.primary.DEFAULT} />}
        title="Contrat, facture, attestations"
      />
      <ClientPaymentInformation />
      <InnerBox className="rounded-lg p-4 bg-white-hover grid grid-cols-2 gap-2 mobile:grid-cols-1">
        <ResourceAccessItem
          mode="pdf"
          label="Facture - confirmation d'inscription"
          disabled={!officialDocumentsAvailability.confirmationInvoice}
          showTooltipIcon={!officialDocumentsAvailability.confirmationInvoice}
          onClick={() =>
            downloadDocuments({
              endpoint: constructApiUrl(API.enrollments.generateConfirmationInvoice.url, {
                id: enrollment.id
              })
            })
          }
        />
        {officialDocumentsAvailability.cancellationInvoice && (
          <ResourceAccessItem
            mode="pdf"
            label="Facture de frais - Annulation séjour"
            onClick={() =>
              downloadDocuments({
                endpoint: constructApiUrl(API.enrollments.generateCancellationInvoice.url, {
                  id: enrollment.id
                })
              })
            }
          />
        )}
        <ResourceAccessItem
          mode="link"
          label="Conditions de vente"
          disabled={!termsAndConditions || isTermsAndConditionsPending}
          onClick={() => {
            if (termsAndConditions) navigate(termsAndConditions.url)
          }}
        />
        {!isCancelled && (
          <ResourceAccessItem
            mode="pdf"
            label="Facture acquittée"
            disabled={!officialDocumentsAvailability.paidInvoice}
            showTooltipIcon={!officialDocumentsAvailability.paidInvoice}
            tooltipContent="Ce document sera disponible à compter du premier jour de séjour de votre enfant"
            onClick={() =>
              downloadDocuments({
                endpoint: constructApiUrl(API.enrollments.generatePaidInvoice.url, {
                  id: enrollment.id
                })
              })
            }
          />
        )}
        {officialDocumentsAvailability.takenInsuranceTypes.map((type) => (
          <ResourceAccessItem
            key={type}
            mode="pdf"
            label={insuranceGeneralProvisionsLabelMap[type]}
            disabled={!officialDocumentsAvailability.generalProvisionsInsurance}
          />
        ))}
        {!isCancelled && (
          <ResourceAccessItem
            mode="pdf"
            label="Attestation de participation"
            disabled={!officialDocumentsAvailability.participationInvoice}
            showTooltipIcon={!officialDocumentsAvailability.participationInvoice}
            tooltipContent="Ce document sera disponible à compter du premier jour de séjour de votre enfant"
            onClick={() =>
              downloadDocuments({
                endpoint: constructApiUrl(API.enrollments.generateParticipationInvoice.url, {
                  id: enrollment.id
                })
              })
            }
          />
        )}
        <ResourceAccessItem
          mode="link"
          label="Nos tarifs comprennent/ne comprennent pas"
          disabled={!officialDocumentsAvailability.pricesDistribution}
        />
        <ResourceAccessItem
          mode="link"
          label="Informations préalables à la réservation"
          disabled={!officialDocumentsAvailability.preBookingInformation}
        />
        <ResourceAccessItem
          mode="link"
          label="Modalités d’organisation des voyages au départ de la Province"
          disabled={!officialDocumentsAvailability.provinceDepartureTravelTerms}
        />
      </InnerBox>
    </div>
  )
}

export default ClientPaymentPage
